import { useCallback, useEffect, useRef, type ReactElement, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '../lib/icons';

export interface ModalProps {
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  /**
   * The prototype's `.modal.wide` (640px). Detail views use it; short forms do
   * not, because a narrow column is easier to fill in.
   */
  wide?: boolean;
}

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * The dialog primitive. Uses the prototype's `overlay` / `modal` classes, so it
 * looks exactly like its 24 hand-built modals did.
 *
 * What it adds is everything the prototype's modals lacked, and none of it is
 * visible to a sighted mouse user:
 *
 * - **A focus trap.** Without one, Tab walks straight out of the dialog and into
 *   the page behind it, which a screen-reader or keyboard user cannot see is
 *   still there. The prototype had no trap in any of its modals.
 * - **`role="dialog"` and `aria-modal`**, so assistive technology announces it
 *   as a dialog and treats the rest of the page as inert.
 * - **`aria-labelledby`**, tying the dialog to its own heading.
 * - **Focus restoration.** On close, focus returns to whatever opened the
 *   dialog. Otherwise it lands back at the top of the document and the user has
 *   to tab all the way to where they were.
 * - **Escape to close**, and a scroll lock on the body.
 */
export function Modal({ title, onClose, children, footer, wide }: ModalProps): ReactElement {
  const { t } = useTranslation();
  const dialogRef = useRef<HTMLDivElement>(null);
  const openerRef = useRef<HTMLElement | null>(null);

  /**
   * Capture the opener, move focus in, and restore it on close — **in one
   * effect**, deliberately.
   *
   * Split across two effects this is subtly broken. React's StrictMode runs
   * setup → cleanup → setup on mount in development, so the capture effect's
   * cleanup fires *after* the focus effect has already run and hands focus
   * straight back to whatever opened the dialog. The dialog then appears with
   * focus still behind it, which is precisely the failure a focus trap exists
   * to prevent. Keeping setup and cleanup balanced in a single effect makes the
   * sequence correct in both development and production.
   */
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    openerRef.current = opener;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // The first control, or the dialog itself when it has none yet — the body
    // may still be loading.
    const first = dialogRef.current?.querySelector<HTMLElement>(FOCUSABLE);
    (first ?? dialogRef.current)?.focus();

    return () => {
      document.body.style.overflow = previousOverflow;
      opener?.focus?.();
    };
  }, []);

  /**
   * Escape is handled on the document, not on the dialog element.
   *
   * A React `onKeyDown` only fires when the event originates inside the dialog,
   * so if focus is ever lost — the opening control unmounts, a click lands on
   * the backdrop, an async render steals it — the dialog becomes impossible to
   * dismiss from the keyboard. A modal must always be escapable.
   */
  useEffect(() => {
    function onDocumentKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
      }
    }

    document.addEventListener('keydown', onDocumentKeyDown);
    return () => document.removeEventListener('keydown', onDocumentKeyDown);
  }, [onClose]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key !== 'Tab') return;

      const focusable = Array.from(
        dialogRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? [],
      ).filter((el) => el.offsetParent !== null);

      if (focusable.length === 0) return;

      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;

      // Wrap at both ends — this is the trap.
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    },
    [onClose],
  );

  const titleId = 'modal-title';

  return createPortal(
    <>
      {/*
        `overlay open`, not `overlay on` — the prototype's rule is
        `.overlay.open { display: grid }`, and with the wrong class the dialog
        mounts but never becomes visible. It is also its own backdrop, so no
        separate scrim element is needed.
      */}
      <div
        className="overlay open"
        // Clicking the backdrop closes, as the prototype does. The target check
        // means a click that merely bubbles up from inside the dialog does not.
        onClick={(event) => {
          if (event.target === event.currentTarget) onClose();
        }}
      >
        <div
          className={wide ? 'modal wide' : 'modal'}
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          tabIndex={-1}
          onKeyDown={handleKeyDown}
        >
          <div className="modal-head">
            <h2 id={titleId}>{title}</h2>
            <button type="button" className="icon-btn" onClick={onClose} aria-label={t('close')}>
              <Icon name="x" />
            </button>
          </div>

          <div className="modal-body">{children}</div>

          {footer ? <div className="modal-foot">{footer}</div> : null}
        </div>
      </div>
    </>,
    document.body,
  );
}
